import * as React from 'react';
import { Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { FieldBox } from './FieldBox';
import { FieldSheet } from './FieldSheet';
import { cn } from '@/lib/utils';

function toMin(hhmm) {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}
function fromMin(total) {
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * S-8 TimeField — FieldBox that opens a 2-column time-chip grid (Momondo-style).
 * Value is a 'HH:MM' string. Chips generated from [minTime, maxTime] by stepMinutes.
 * A value not on the step grid is still shown and selectable.
 */
export function TimeField({
  label,
  value,
  onChange,
  stepMinutes = 30,
  minTime = '00:00',
  maxTime = '23:30',
  disabledTimes,
  placeholder,
  disabled = false,
  error = false,
  size = 'lg',
  testId,
  className,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const options = React.useMemo(() => {
    const start = toMin(minTime);
    const end = toMin(maxTime);
    const step = Math.max(5, stepMinutes || 30);
    const out = [];
    for (let m = start; m <= end; m += step) out.push(fromMin(m));
    // ensure the current value is present even if off-grid
    if (value && !out.includes(value) && toMin(value) >= start && toMin(value) <= end) {
      out.push(value);
      out.sort((a, b) => toMin(a) - toMin(b));
    }
    return out;
  }, [minTime, maxTime, stepMinutes, value]);

  const disabledSet = disabledTimes instanceof Set ? disabledTimes : new Set(disabledTimes || []);

  const handlePick = (v) => {
    onChange?.(v);
    setOpen(false);
  };

  const trigger = (
    <FieldBox
      label={label}
      value={value}
      placeholder={placeholder ?? t('fields.select_time', 'Select time')}
      icon={Clock}
      active={open}
      error={error}
      disabled={disabled}
      size={size}
      testId={testId}
      className={className}
    />
  );

  return (
    <FieldSheet
      open={open}
      onOpenChange={setOpen}
      trigger={trigger}
      title={label || t('fields.select_time', 'Select time')}
      desktopWidth="w-72"
      testId={testId}
    >
      <div
        className="grid grid-cols-2 gap-2.5 md:max-h-[300px] md:overflow-y-auto md:pr-1"
        data-testid={testId ? `${testId}-grid` : undefined}
      >
        {options.map((v) => {
          const isSel = v === value;
          const isDis = disabledSet.has(v);
          return (
            <button
              key={v}
              type="button"
              disabled={isDis}
              onClick={() => handlePick(v)}
              data-testid={testId ? `${testId}-chip-${v}` : undefined}
              className={cn(
                'flex items-center justify-center py-3 px-3 rounded-xl text-sm font-semibold border transition-colors active:scale-95',
                isSel
                  ? 'bg-[#FF6B35] border-[#FF6B35] text-white shadow-md shadow-[#FF6B35]/30'
                  : isDis
                  ? 'bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed'
                  : 'bg-white border-slate-200 text-slate-700 hover:border-[#FF6B35] hover:text-[#FF6B35]'
              )}
            >
              {v}
            </button>
          );
        })}
      </div>
    </FieldSheet>
  );
}

export default TimeField;
